// Storefront v2 chrome (planning/022-storefront-v2 stage 07): header hide-on-scroll (M07), mega
// menu (M23), drawers and sheets (M32), cart drawer, search overlay (M24). No copy in here: every
// string comes from Liquid (data attributes or server-rendered sections).

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Every open dialog marks the rest of the page inert; the last one closed restores it. A dialog
 * that lives inside an inert target (the collection's filter sheet is in <main>) can't sit in an
 * inert subtree, so that target stays live and everything around the dialog goes inert instead.
 */
const openDialogs = new Set();
const inerted = new Set();
function setPageInert(on) {
  for (const el of inerted) el.removeAttribute('inert');
  inerted.clear();
  const mark = (el) => {
    if (el.hasAttribute('inert')) return;
    el.setAttribute('inert', '');
    inerted.add(el);
  };
  if (on) {
    for (const target of document.querySelectorAll('[data-am-inert-target]')) {
      const inside = [...openDialogs].find((d) => target.contains(d));
      if (!inside) {
        mark(target);
        continue;
      }
      for (let node = inside; node !== target; node = node.parentElement) {
        for (const sibling of node.parentElement.children) if (sibling !== node) mark(sibling);
      }
    }
  }
  document.documentElement.classList.toggle('am-scroll-lock', on);
}

/**
 * <am-dialog id="..." data-label-close>: drawer, sheet, full-screen menu or search overlay.
 * Focus trap, Esc, backdrop click, inert page, scroll lock, focus return (guide §03 Drawers).
 * Opened by any element with [data-am-open="<id>"], closed by [data-am-close] inside it.
 */
class AmDialog extends HTMLElement {
  connectedCallback() {
    // a re-render can move an open dialog back into the page: wire the listeners only once
    if (this.wired) return;
    this.wired = true;
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-am-close]') || e.target.matches('[data-am-backdrop]')) {
        e.preventDefault();
        this.close();
      }
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      } else if (e.key === 'Tab') this.trap(e);
    });
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  // looked up each time: a section re-render can swap the panel while the dialog is open
  get panel() {
    return this.querySelector('[data-am-panel]') || this;
  }

  /** Re-applies the inert page after the DOM around an open dialog was replaced. */
  relock() {
    if (this.isOpen) setPageInert(true);
  }

  open(opener) {
    if (this.isOpen) return;
    this.opener = opener || document.activeElement;
    // the dialog itself must not be inside an inert subtree
    this.removeAttribute('inert');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    openDialogs.add(this);
    setPageInert(true);
    document.querySelectorAll(`[data-am-open="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const target = this.querySelector('[data-am-autofocus]') || this.querySelector(FOCUSABLE);
    requestAnimationFrame(() => target && target.focus());
    this.dispatchEvent(new CustomEvent('am:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    openDialogs.delete(this);
    setPageInert(openDialogs.size > 0);
    document.querySelectorAll(`[data-am-open="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    // the opener may have been re-rendered meanwhile (the cart count is), so fall back to the
    // current element that opens this dialog
    const opener = this.opener?.isConnected ? this.opener : document.querySelector(`[data-am-open="${this.id}"]`);
    opener?.focus();
    this.dispatchEvent(new CustomEvent('am:close', { bubbles: true }));
  }

  trap(e) {
    const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-am-open]');
  if (!opener) return;
  const dialog = document.getElementById(opener.dataset.amOpen);
  if (!dialog || typeof dialog.open !== 'function') return;
  e.preventDefault();
  dialog.open(opener);
});

/** <am-header>: sticky, hides after scrolling down 120px, returns on scroll up (M07). */
class AmHeader extends HTMLElement {
  connectedCallback() {
    this.lastY = window.scrollY;
    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  update() {
    this.ticking = false;
    const y = window.scrollY;
    const busy = openDialogs.size > 0 || this.contains(document.activeElement) || this.querySelector('am-mega-menu[open]');
    this.classList.toggle('is-scrolled', y > 4);
    if (!reduced() && !busy && y > 120 && y > this.lastY + 4) this.classList.add('is-hidden');
    else if (y < this.lastY - 4 || y <= 120 || busy) this.classList.remove('is-hidden');
    this.lastY = y;
  }
}

/** <am-mega-menu>: disclosure; opens on hover after 120ms, click or Enter; Esc closes (M23). */
class AmMegaMenu extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-am-mega-trigger]');
    this.panel = this.querySelector('[data-am-mega-panel]');
    if (!this.trigger || !this.panel) return;
    const canHover = window.matchMedia('(hover: hover)').matches;
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle(!this.hasAttribute('open'));
    });
    if (canHover) {
      this.addEventListener('pointerenter', () => this.schedule(true, 120));
      this.addEventListener('pointerleave', () => this.schedule(false, 200));
    }
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.toggle(false);
        this.trigger.focus();
      }
    });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.toggle(false);
    });
  }

  schedule(open, delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.toggle(open), delay);
  }

  toggle(open) {
    clearTimeout(this.timer);
    this.toggleAttribute('open', open);
    this.trigger.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('am-mega-open', open);
  }
}

/** Section Rendering API helper: fetch sections' HTML and swap them in place. */
async function renderSections(ids, url = window.location.pathname) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('sections', ids.join(','));
  // No `Accept: application/json`: on /products/* and /cart Shopify answers that with the product or
  // cart JSON instead of the sections, and the drawer would keep its stale body (stage 12 finding).
  const res = await fetch(u);
  if (!res.ok) throw new Error(`sections ${res.status}`);
  const json = await res.json();
  const missing = ids.filter((id) => typeof json[id] !== 'string');
  if (missing.length) throw new Error(`sections missing: ${missing.join(',')}`);
  return json;
}

function swapFrom(html, selector) {
  const next = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  const current = document.querySelector(selector);
  if (next && current) current.replaceWith(next);
}

/**
 * <am-cart>: the cart drawer body. Quantity and remove go through /cart/change.js, then the
 * drawer, header count and cart page (if present) are re-rendered server-side so the copy,
 * packaging lines and the two-parcel note stay in Liquid.
 */
class AmCart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-am-qty]');
      if (!btn) return;
      e.preventDefault();
      const line = btn.closest('[data-line-key]');
      const current = Number(line.dataset.qty);
      const delta = btn.dataset.amQty;
      const qty = delta === 'remove' ? 0 : Math.max(0, current + Number(delta));
      this.change(line.dataset.lineKey, qty, btn);
    });
    this.loadUpsell();
  }

  async change(key, quantity, btn) {
    btn.setAttribute('aria-busy', 'true');
    // M51: the line sweeps if the change takes longer than 200ms (the re-render replaces it)
    btn.closest('[data-line-key]')?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error(`cart ${res.status}`);
      await refreshCart(false);
    } catch {
      btn.removeAttribute('aria-busy');
      this.querySelector('[data-am-cart-error]')?.removeAttribute('hidden');
    }
  }

  /** The cart upsell (am.cart.upsell): a related product, same artist first, from the recommendations API. */
  async loadUpsell() {
    const slot = this.querySelector('[data-am-upsell]');
    if (!slot || !slot.dataset.productId) return;
    try {
      const u = new URL(`${window.Shopify?.routes?.root || '/'}recommendations/products`, window.location.origin);
      u.searchParams.set('product_id', slot.dataset.productId);
      u.searchParams.set('limit', '8');
      u.searchParams.set('section_id', 'am-cart-upsell');
      const res = await fetch(u);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const inCart = new Set((slot.dataset.inCart || '').split(','));
      const candidates = [...doc.querySelectorAll('[data-upsell-product]')].filter((el) => !inCart.has(el.dataset.upsellProduct));
      // same artist first, then anything related
      const pick = candidates.find((el) => el.dataset.vendor === slot.dataset.vendor) || candidates[0];
      if (pick) {
        const kicker = this.querySelector('template[data-am-upsell-kicker]');
        const head = document.createElement('p');
        head.className = 'am-mega__head';
        head.textContent = kicker ? kicker.content.textContent.trim() : '';
        slot.replaceChildren(head, pick);
        slot.removeAttribute('hidden');
      }
    } catch {
      /* upsell is optional */
    }
  }
}

async function refreshCart(open = true) {
  const ids = ['am-cart-drawer'];
  const page = document.querySelector('[data-am-cart-page]');
  if (page) ids.push(page.dataset.sectionId);
  const html = await renderSections(ids);
  swapFrom(html['am-cart-drawer'], '#am-cart-drawer [data-am-cart-body]');
  swapFrom(html['am-cart-drawer'], 'template[data-am-cart-count-next]');
  if (page) swapFrom(html[page.dataset.sectionId], '[data-am-cart-page]');
  // The header lives in a section group (generated section IDs), so the drawer section carries
  // the fresh count link in a <template> and every header count is replaced from it.
  const next = document.querySelector('template[data-am-cart-count-next]');
  if (next) {
    for (const count of document.querySelectorAll('[data-am-cart-count]')) {
      const fresh = next.content.firstElementChild.cloneNode(true);
      count.replaceWith(fresh);
      // M31: the count bumps once the drawer has slid in (420ms)
      if (!reduced()) setTimeout(() => fresh.classList.add('is-bumped'), open ? 420 : 0);
    }
  }
  const drawer = document.getElementById('am-cart-drawer');
  // an add opens the drawer (announced as a dialog); a change inside it is read from the status line
  const status = drawer?.querySelector('[data-am-cart-status]');
  if (status && !open) status.textContent = document.querySelector('[data-am-cart-count]')?.getAttribute('aria-label') ?? '';
  if (open) drawer?.open();
  // a swapped body drops focus to <body>; keep it inside the open drawer (Esc, focus trap)
  else if (drawer?.isOpen && !drawer.contains(document.activeElement)) drawer.querySelector(FOCUSABLE)?.focus();
}

// Esc closes the most recently opened dialog even when focus has fallen out of it.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !openDialogs.size) return;
  [...openDialogs].pop().close();
});

// Horizon's product forms (until stage 08 replaces the PDP) and v2's own forms announce adds.
document.addEventListener('cart:update', () => refreshCart(true).catch(() => {}));
document.addEventListener('am:cart-added', () => refreshCart(true).catch(() => {}));

/**
 * <am-search>: predictive search inside the search overlay (M24). Debounced 200ms, results
 * rendered by sections/am-predictive-search.liquid. "/" opens the overlay from anywhere.
 */
class AmSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-am-search-results]');
    this.idle = this.querySelector('[data-am-search-idle]');
    if (!this.input) return;
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 200);
    });
  }

  async search(q) {
    this.controller?.abort();
    if (!q) {
      this.results.replaceChildren();
      this.idle?.removeAttribute('hidden');
      return;
    }
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    try {
      const u = new URL(`${window.Shopify?.routes?.root || '/'}search/suggest`, window.location.origin);
      u.searchParams.set('q', q);
      u.searchParams.set('resources[type]', 'product,query');
      u.searchParams.set('resources[limit]', '8');
      u.searchParams.set('section_id', 'am-predictive-search');
      const res = await fetch(u, { signal: this.controller.signal });
      // 429 (Shopify bot protection) or any error: keep the idle panel; Enter still submits the form
      if (!res.ok) throw new Error(`suggest ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const body = doc.querySelector('[data-am-search-body]');
      this.idle?.setAttribute('hidden', '');
      this.results.replaceChildren(...(body ? [body] : []));
    } catch (err) {
      if (err.name !== 'AbortError') {
        this.results.replaceChildren();
        this.idle?.removeAttribute('hidden');
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('keydown', (e) => {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
  const t = e.target;
  if (t.closest('input, textarea, select, [contenteditable="true"]')) return;
  const overlay = document.getElementById('am-search-overlay');
  if (!overlay) return;
  e.preventDefault();
  overlay.open();
});

customElements.define('am-dialog', AmDialog);
customElements.define('am-header', AmHeader);
customElements.define('am-mega-menu', AmMegaMenu);
customElements.define('am-cart', AmCart);
customElements.define('am-search', AmSearch);
